import store from '@/store';
import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';
import { metresToViewerUnits, resolveViewerUnitScale, scaleFromDisplayUnit } from '@/utils/viewerUnits';

const THREE = window.THREE;

/**
 * Coordinate-space authority for PERSISTED writes (AVA stores exactly ONE space
 * in the DB: global metres = (viewerPoint + globalOffset) × unitScale).
 *
 * Why this module exists: unitScale historically had two disagreeing sources —
 * the user-confirmed facility displayUnit and the model's self-declared
 * getUnitScale() (which returns 1 for mis-declared SVF re-uploads, the root
 * cause of the Register-2D "No Level found" mm/m corruption). Every function
 * here treats the displayUnit as the authority and the live model value as a
 * suggestion, and fails loudly instead of falling back silently.
 */

// Single source of truth for display-unit string → metres-per-model-unit scale.
// The store's setDisplayUnit mutation delegates to this table.
export { scaleFromDisplayUnit };

export class CoordinateSpaceError extends Error {
  constructor(message, info = {}) {
    super(message);
    this.name = 'CoordinateSpaceError';
    this.info = info;
  }
}

/**
 * Resolve the unit scale to use for persisted coordinate writes.
 * Authority order: facility displayUnit (user-confirmed) → current store value
 * (seeded from saved DB metadata) → live model declaration.
 * `conflict` is true when the displayUnit and the live model disagree — the
 * mis-declared-model signature.
 */
export function resolvePersistUnitScale(model = null) {
  const displayUnit = store.state.displayUnit;
  const fromDisplayUnit = scaleFromDisplayUnit(displayUnit);
  const fromStore = store.state.unitScale || 0;
  const liveModel = model || store.state.viewer?.model;
  const fromModel = liveModel?.getUnitScale?.() || 0;

  const scale = fromDisplayUnit || fromStore || fromModel || 0;
  const source = fromDisplayUnit ? 'displayUnit' : fromStore ? 'store' : fromModel ? 'model' : 'none';
  const conflict =
    !!fromDisplayUnit && !!fromModel && Math.abs(fromDisplayUnit - fromModel) > fromDisplayUnit * 0.01;

  return { scale, source, conflict, displayUnit, modelScale: fromModel };
}

/**
 * Gate for every flow that persists world coordinates (harvest, tag
 * create/edit children, remap, center heal, Min/Max processing).
 * Throws CoordinateSpaceError when no usable scale exists; warns (but
 * proceeds with the displayUnit) when the model disagrees — the displayUnit
 * is the user-confirmed authority.
 */
export function assertPersistableUnitScale(model = null) {
  const resolution = resolvePersistUnitScale(model);

  debugLog('[CoordAuthority] persist-scale resolution', resolution);

  if (!resolution.scale) {
    throw new CoordinateSpaceError(
      'Model units are not set for this facility — set the facility display unit before saving 3D positions.',
      resolution,
    );
  }

  if (resolution.conflict) {
    debugWarn(
      `[CoordAuthority] Unit mismatch: facility display unit '${resolution.displayUnit}' ` +
        `(scale ${resolution.scale}) vs model-declared scale ${resolution.modelScale}. ` +
        'Proceeding with the facility unit (user-confirmed authority). ' +
        'If the new model version really changed units, update the facility display unit.',
    );
  }

  return resolution;
}

/**
 * Convert a viewer-space point to the global space stored in the DB.
 * Pass a pre-resolved `scale` when converting in a loop (resolve once via
 * assertPersistableUnitScale, then reuse); without it the scale is resolved
 * and asserted per call.
 */
export function viewerToGlobal(point, scale = null, model = null) {
  const effectiveScale = scale || assertPersistableUnitScale(model).scale;
  const p =
    point instanceof THREE.Vector3 ? point.clone() : new THREE.Vector3(point.x, point.y, point.z);
  return p.add(store.state.globalOffset).multiplyScalar(effectiveScale);
}

/**
 * Metres → viewer units for sizes and distances in the live viewer (crop boxes,
 * zoom padding, tolerances). Uses the scale the viewer is running in, falling
 * back to the facility display unit before the viewer has loaded. Throws
 * CoordinateSpaceError when no unit is known, so callers can tell the user
 * instead of drawing a box 1000× too small or too big.
 */
export function metresToViewerLength(metres, model = null) {
  const liveModel = model || store.state.viewer?.model;
  const scale = resolveViewerUnitScale({
    viewerScale: store.state.unitScale,
    displayUnit: store.state.displayUnit,
    modelScale: liveModel?.getUnitScale?.(),
  });
  try {
    return metresToViewerUnits(metres, scale);
  } catch (err) {
    throw new CoordinateSpaceError(err.message, { displayUnit: store.state.displayUnit, scale });
  }
}

/** Axis-aligned cube in viewer space centred on `point`, with a half-size in metres. */
export function viewerCubeAround(point, halfSizeMetres, model = null) {
  const half = metresToViewerLength(halfSizeMetres, model);
  return new THREE.Box3(
    new THREE.Vector3(point.x - half, point.y - half, point.z - half),
    new THREE.Vector3(point.x + half, point.y + half, point.z + half),
  );
}

/**
 * Bulk-write sanity check: a converted elevation must land near the facility's
 * level bands when levels exist. Catches the case the authority alone cannot —
 * a stale displayUnit and a mis-declared model AGREEING on the wrong unit
 * (z ≈ 30163 against bands 29…50 = the original corruption signature).
 * No-op when the facility has no levels to compare against.
 */
export function assertElevationSane(globalPoint, marginMeters = 50) {
  const levels = store.state.levels || [];
  if (!levels.length) return;

  const bottoms = levels.map((l) => l.bottomElevation).filter(Number.isFinite);
  const tops = levels.map((l) => l.topElevation).filter(Number.isFinite);
  if (!bottoms.length || !tops.length) return;

  const min = Math.min(...bottoms) - marginMeters;
  const max = Math.max(...tops) + marginMeters;

  if (globalPoint.z < min || globalPoint.z > max) {
    throw new CoordinateSpaceError(
      `Converted elevation ${globalPoint.z.toFixed(1)} m is far outside the facility levels ` +
        `(${min.toFixed(1)}…${max.toFixed(1)} m) — the unit scale is likely wrong. ` +
        'Verify the facility display unit before saving 3D positions.',
      { z: globalPoint.z, min, max },
    );
  }
}
